import { type FC, useState, useEffect } from 'react';
import { Container, Box, Snackbar, Alert as MuiAlert } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchSiteImages, fetchSites } from '@/features/sites/sitesThunks';
import { SelectSiteImages } from '@/features/sites/sitesSelector';
import { useSelector } from 'react-redux';
import { selectAuthUser } from '@/features/auth/authSelectors';
import { UserRole } from '@/types/user.types';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import LoadingOverlay from '@/components/common/LoadingOverlay';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useImageLoader } from './hooks/useImageLoader';
import { useSiteFilter } from './hooks/useSiteFilter';
import { useImageUpload } from './hooks/useImageUpload';
import { useImageDelete } from './hooks/useImageDelete';
import GalleryHeader from './components/GalleryHeader';
import SiteFilterList from './components/SiteFilterList';
import ImageGallery from './components/ImageGallery';
import ImagePreviewDialog from './components/ImagePreviewDialog';

const CampusGallery: FC = () => {
    const dispatch = useAppDispatch();
    const snackbar = useSnackbar();
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    const { sites, loading, error } = useAppSelector((state) => state.sites);
    const images = useSelector(SelectSiteImages);
    const authUser = useSelector(selectAuthUser);
    const isSuperAdmin = authUser?.role === UserRole.SUPERADMIN || authUser?.role === UserRole.DIRECTIVE;

    const { selectedSite, handleSiteClick, handleClearFilter } = useSiteFilter();
    
    const { uploadingSiteId, isUploading, handleAddPhotos } = useImageUpload({
        selectedSite,
        authUserId: authUser?.uid || authUser?.email,
        snackbar
    });

    const {
        deleteDialogOpen,
        imageToDelete,
        isDeleting,
        handleDeleteImage,
        confirmDelete,
        cancelDelete
    } = useImageDelete({
        images,
        selectedSite,
        snackbar
    });

    const { imagesWithUrls, isLoadingImages } = useImageLoader(images);

    // Fetch initial data
    useEffect(() => {
        dispatch(fetchSites());
        dispatch(fetchSiteImages(null));
    }, [dispatch]);

    // Image preview handlers
    const handleImageClick = (loadedUrl: string | null) => {
        if (loadedUrl) {
            setSelectedImage(loadedUrl);
        }
    };

    const handleCloseDialog = () => {
        setSelectedImage(null);
    };

    // Get selected site name for display
    const selectedSiteName = sites.find(s => s.id === selectedSite)?.Name || 'Sede seleccionada';

    return (
        <Box sx={{ backgroundColor: '#F8F9FA', minHeight: '100vh', px: { xs: 4, sm: 6, md: 8 } }}>
            {/* Loading Overlays */}
            {isDeleting && <LoadingOverlay message="Eliminando imagen, por favor espere..." />}
            {isUploading && <LoadingOverlay message="Subiendo imagen, por favor espere..." />}
            {isLoadingImages && images.length > 0 && <LoadingOverlay message="Cargando imágenes..." />}

            {/* Snackbar Notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={snackbar.close}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <MuiAlert onClose={snackbar.close} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={deleteDialogOpen}
                title="Confirmar eliminación"
                message="¿Estás seguro de que quieres eliminar esta imagen?"
                itemName={imageToDelete ? `Imagen: ${imageToDelete.split('/').pop()?.substring(0, 30)}...` : ''}
                warningMessage="Esta acción no se puede deshacer. La imagen será eliminada permanentemente de la galería."
                confirmText="Eliminar"
                cancelText="Cancelar"
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                confirmColor="error"
                isLoading={isDeleting}
            />

            <Container
                maxWidth="xl"
                sx={{
                    pt: { xs: 4, sm: 6, md: 8, lg: 10 },
                    pb: { xs: 3, sm: 4, md: 5, lg: 6 },
                    px: { xs: 1, sm: 1.5, md: 2 }
                }}
            >
                {/* Header Section */}
                <GalleryHeader />

                {/* Site Filter List */}
                <Box sx={{ mb: { xs: 4, sm: 5, md: 6 } }}>
                    <SiteFilterList
                        sites={sites}
                        loading={loading}
                        error={error}
                        selectedSite={selectedSite}
                        uploadingSiteId={uploadingSiteId}
                        isSuperAdmin={isSuperAdmin}
                        onSiteClick={handleSiteClick}
                        onClearFilter={handleClearFilter}
                        onAddPhotos={handleAddPhotos}
                    />
                </Box>

                {/* Image Gallery */}
                <Box>
                    <ImageGallery
                        images={images}
                        imagesWithUrls={imagesWithUrls}
                        selectedSite={selectedSite}
                        siteName={selectedSiteName}
                        isSuperAdmin={isSuperAdmin}
                        currentUserId={authUser?.uid || authUser?.email || ''}
                        onImageClick={handleImageClick}
                        onDeleteImage={handleDeleteImage}
                        onClearFilter={handleClearFilter}
                    />
                </Box>

                {/* Image Preview Dialog */}
                <ImagePreviewDialog
                    imageUrl={selectedImage}
                    onClose={handleCloseDialog}
                />
            </Container>
        </Box>
    );
};

export default CampusGallery;
